<template>
    <LoadingOverlay :loading="loading" />

    <div class="wtr-page">
        <div class="wtr-inner">

            <!-- Page title. The ladder below it is the main thing on this page. -->
            <div class="wtr-title">
                <svg class="wtr-title-icon" width="38" height="38" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="5" />
                    <circle cx="12" cy="12" r="1.4" fill="currentColor" />
                </svg>
                <h1>Target &amp; Reward</h1>
                <span class="wtr-title-bar"></span>
            </div>

            <div class="wtr-ladder">
                <div v-for="tier in tiers" :key="tier.key" class="wtr-tier" :class="{ 'is-top': tier.isTop }">
                    <span v-if="tier.isTop" class="wtr-tier-flag">Top tier</span>

                    <div class="wtr-tier-col">
                        <span class="wtr-tier-cap">Target</span>
                        <span class="wtr-tier-target">
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="9.6" fill="#F7C948" />
                                <circle cx="12" cy="12" r="6.3" fill="none" stroke="#D79A16" stroke-width="1.6" />
                            </svg>
                            <span class="wtr-num">{{ tier.target }}</span>
                        </span>
                    </div>

                    <svg class="wtr-tier-arrow" width="22" height="14" viewBox="0 0 22 14" fill="none"
                        stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M2 7h16" />
                        <path d="M13.5 2.5L19 7l-5.5 4.5" />
                    </svg>

                    <div class="wtr-tier-col wtr-tier-col--end">
                        <span class="wtr-tier-cap">You get</span>
                        <span class="wtr-tier-reward wtr-num" :style="tier.rewardStyle">{{ tier.reward }}</span>
                    </div>
                </div>
            </div>

            <p class="wtr-ladder-note">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                    stroke-linecap="round">
                    <path d="M12 3v18" />
                    <path d="M17 8H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H7" />
                </svg>
                Reward is based on your total weekly income
            </p>

            <!-- What counts towards the target -->
            <div class="wtr-counts">
                <div class="wtr-count wtr-count--upto">
                    <span class="wtr-count-chip">Up to 500K</span>
                    <p>Call earnings, gifts and game tips all count towards your reward.</p>
                </div>
                <div class="wtr-count wtr-count--above">
                    <span class="wtr-count-chip">1M and above</span>
                    <p>Only gifts and game tips are counted.</p>
                </div>
            </div>

            <!-- Eligibility rules -->
            <div class="wtr-rules">
                <div class="wtr-rules-head">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M9 11l2.2 2.2L15.5 9" />
                        <rect x="3.2" y="3.2" width="17.6" height="17.6" rx="5" />
                    </svg>
                    <span>Rules for eligibility</span>
                </div>

                <ul>
                    <li v-for="(rule, i) in rules" :key="i">{{ rule }}</li>
                </ul>
            </div>

            <!-- Closing -->
            <div class="wtr-closing">
                <h5>The more you work, the more you earn</h5>
                <p>Every effort counts, and every hour brings you closer to bigger rewards.</p>
            </div>

        </div>
    </div>
</template>

<script>
import axios from "axios";
import './WeeklyTargetRuleStyle.css'

import LoadingOverlay from '../../components/LoadingOverlay.vue'

export default {
    name: 'WeeklyTargetRuleDynamic',

    components: { LoadingOverlay },

    data() {
        return {
            loading: false,
            freeTargetData: [],
            rules: [
                'Streaming is available 24/7.',
                'Stream and take calls for up to 5 hours a day, reaching 18 hours across the week.',
                'The calculation period runs Monday 00:00 to Sunday 23:59.',
                'For example, weekly earnings of 1M earn you an extra 250K on top.',
            ],
        };
    },

    computed: {
        // Emphasis grows down the ladder, so the biggest reward reads loudest
        // and the final tier is called out.
        tiers() {
            const rows = this.freeTargetData;
            const lastIdx = rows.length - 1;

            return rows.map((row, i) => {
                const weight = lastIdx > 0 ? i / lastIdx : 1;
                return {
                    key: row.id ?? `${row.coin}-${row.reward}`,
                    isTop: i === lastIdx,
                    target: this.formatNumber(row.coin),
                    reward: this.formatNumber(row.reward),
                    rewardStyle: { fontSize: `${Math.round(19 + 6 * (0.55 + 0.45 * weight))}px` },
                };
            });
        },
    },

    mounted() {
        this.fetchThisWeekData();
    },

    methods: {
        async fetchThisWeekData() {
            this.loading = true;

            try {
                const tokenParam = this.$route.params.token || "";
                const token = tokenParam.includes("=")
                    ? tokenParam.split("=")[0]
                    : tokenParam;

                const response = await axios.get('api/get-free-target-list', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                this.freeTargetData = response?.data?.result || [];

            } catch (error) {
                console.error("Fetch Error:", error?.message || error);
            } finally {
                this.loading = false;
            }
        },

        formatNumber(num = 0) {
            if (num >= 1_000_000) return `${this.trimZeros(num / 1_000_000)}M`;
            if (num >= 1_000) return `${this.trimZeros(num / 1_000)}K`;
            return String(num);
        },

        // 2.5 must render as "2.5", not "2.50" — parseFloat drops any trailing
        // zeros, where the old `.replace(/\.00$/, '')` only caught whole numbers.
        trimZeros(value) {
            return Number.isInteger(value) ? String(value) : String(parseFloat(value.toFixed(2)));
        },
    }
}
</script>
